import React, { useState, useRef, useEffect, memo, Dispatch, SetStateAction } from 'react';
import { AvatarImage } from '../../layout/AvatarImage';
import { ProfileResponse } from '../../../utils/types';
import { JWBadge } from '@/components/Badge/JWBadge';  // Import JWBadge
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import MiniJWCarousel from '../../brand/JWCarousel/MiniJWCarousel';
import { MiniJWCarouselSlideProps } from '../../brand/JWCarousel/MiniJWCarousel';
import { HighlightType } from '../../../types/profile';
import { ChevronLeft, ChevronRight, Music, Zap } from 'lucide-react';
import { likeUser, unlikeUser } from '../../../api/user';
import { applyLikedUser, useLikedUserSet } from '@/hooks/likedUsers';
import { toast } from 'react-toastify';
import LikeButton from '../../Profile/LikeButton';
import { getSimilarityConfig } from '../../../api/similarity'; // Import the API function
import CommonInterestButton from './CommonInterestButton';
import { getUserDisplayName } from '@/utils/userDisplay';
import ProfilePreviewHeader from './ProfilePreviewHeader';
import ProfilePreviewSkills from './ProfilePreviewSkills';
import { useMediaQuery, useVisibleSkillCount } from '@/hooks/usePerformanceOptimizations';
import { JamBoardPin } from '../../../types/jamboard';
import { fetchJamBoardPins } from '../../../api/jamboard';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
    getModalCurrentIndex,
    getModalNavigableHighlights,
} from '../../../utils/highlightModalNavigation';
import { profileSkillChipClassName } from '../../../pages/Onboarding/OnboardingUI';
import { skillChipHref } from '@/utils/seoSlug';
import './profilePreviewList.css';

function blockNestedControls(event: React.MouseEvent) {
    const target = event.target as HTMLElement | null;
    if (target?.closest('.like-button, button')) event.preventDefault();
}

// Add custom CSS for animations
const animationStyles = `
  @keyframes blurIn {
    0% { 
      filter: blur(10px);
    }
    85% { 
      filter: blur(8px);
    }
    92% { 
      filter: blur(5px);
    }
    96% { 
      filter: blur(2px);
    }
    100% { 
      filter: blur(0);
    }
  }
  
  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.1); }
    100% { transform: scale(1); }
  }
  
  .animate-interest-button {
    animation: blurIn 3s ease-out forwards;
  }
  
  .animate-fadeIn {
    animation: fadeIn 0.5s ease-out forwards;
  }
  
  .group-hover\\:animate-pulse:hover {
    animation: pulse 1.5s infinite;
  }
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

// Extend ProfileResponse type to include favArtists and favGenres
interface ExtendedProfileResponse extends ProfileResponse {
  favArtists?: Array<string | { name?: string; displayText?: string; id?: string; url?: string }>;
  favGenres?: string[];
  featuredJamBoardPinId?: string | null;
  jamBoardPinsDisplayMode?: 'none' | 'most_recent' | 'all';
  hiddenJamBoardPinIds?: string[];
}

interface ProfilePreviewProps {
  profile: ExtendedProfileResponse;
  isGridView?: boolean;
  onHighlightClick?: (
    highlight: HighlightType,
    index: number,
    profileId: string,
    highlights: HighlightType[]
  ) => void;
  onUnlike?: (userId: string) => void;  // Add onUnlike callback prop
  hideLikeButton?: boolean; // Add hideLikeButton prop
  profileIndex?: number; // Add profileIndex prop
}

// Custom equality function for memo optimization
const areEqual = (prevProps: ProfilePreviewProps, nextProps: ProfilePreviewProps) => {
    // Compare profile IDs
    if (prevProps.profile._id !== nextProps.profile._id) return false;

    // Compare view mode
    if (prevProps.isGridView !== nextProps.isGridView) return false;

    // Compare important profile data
    if (prevProps.profile.updatedAt !== nextProps.profile.updatedAt) return false;

    // Compare highlights length (for re-render if highlights change)
    if (prevProps.profile.highlights?.length !== nextProps.profile.highlights?.length) return false;

    // If we got here, the component doesn't need to re-render
    return true;
};

// Type for artist objects that might be in the favArtists array
type ArtistType = string | { name?: string; displayText?: string; id?: string; url?: string };

type CommonInterestType = { type: string, value: string } | null;
type CommonInterestsMap = Record<string, CommonInterestType>;

const ProfilePreview = ({ profile, isGridView = false, onHighlightClick, onUnlike, hideLikeButton = false, profileIndex = 0 }: ProfilePreviewProps) => {
    const navigate = useNavigate();
    const skillsContainerRef = useRef<HTMLDivElement>(null);

    // Replace expensive useState with performance hooks
    const isMobile = useMediaQuery('(max-width: 640px)');
    const visibleSkillCount = useVisibleSkillCount(skillsContainerRef, profile.skills || undefined, isGridView);

    const [newUserThreshold, setNewUserThreshold] = useState(24);
    const [preLoadedCommonInterests, setPreLoadedCommonInterests] = useState<CommonInterestsMap>({});
    const { data: allJamBoardPins = [] } = useQuery({
        queryKey: ['jamboard', 'pins'],
        queryFn: fetchJamBoardPins,
    });

    // Get the logged-in user's profile from Redux state
    const loggedInUserProfile = useSelector((state: RootState) => state.auth.profile);
    const user = useSelector((state: RootState) => state.auth.user);
    const queryClient = useQueryClient();
    const likedUserIds = useLikedUserSet();
    const likeStatus = Boolean(profile.userId && likedUserIds.has(String(profile.userId)));
    const profileOwnerId = profile.userId || (profile as { userData?: { _id?: string } }).userData?._id;
    const isOwnProfile = Boolean(user?._id && profileOwnerId && String(profileOwnerId) === String(user._id));

    /**
   * Find a common interest between current user and the profile being viewed
   * Prioritizes artists over genres
   */
    const findCommonInterest = (
        currentUserProfile: ExtendedProfileResponse | null,
        viewedProfile: ExtendedProfileResponse,
        isPreLoading: boolean = false
    ) => {
    // If either profile is missing, return null
        if (!currentUserProfile || !viewedProfile) return null;

        // If we're pre-loading, use the pre-loaded value if it exists
        if (isPreLoading && preLoadedCommonInterests[viewedProfile._id]) {
            return preLoadedCommonInterests[viewedProfile._id];
        }

        // Check for matching artists first (priority)
        if (currentUserProfile.favArtists?.length && viewedProfile.favArtists?.length) {
            // Extract artist names from current user's profile
            const currentUserArtists = currentUserProfile.favArtists.map((artist: ArtistType) => {
                // Handle different artist object structures
                if (typeof artist === 'string') return artist.toLowerCase();
                if (typeof artist === 'object' && artist !== null) {
                    return (artist.name || artist.displayText || '').toLowerCase();
                }
                return '';
            }).filter(Boolean);

            // Extract artist names from viewed profile
            const viewedProfileArtists = viewedProfile.favArtists.map((artist: ArtistType) => {
                if (typeof artist === 'string') return artist.toLowerCase();
                if (typeof artist === 'object' && artist !== null) {
                    return (artist.name || artist.displayText || '').toLowerCase();
                }
                return '';
            }).filter(Boolean);

            // Find matching artists by comparing lowercase names
            const matchingArtists = currentUserArtists
                .filter((userArtist: string) => viewedProfileArtists.includes(userArtist))
                .map((matchedArtist: string) => {
                    // Get the original casing from the viewed profile
                    const viewedProfileArtistObj = viewedProfile.favArtists?.find((artist: ArtistType) => {
                        if (typeof artist === 'string') {
                            return artist.toLowerCase() === matchedArtist;
                        }
                        return (
                            (artist as any)?.name?.toLowerCase() === matchedArtist ||
                            (artist as any)?.displayText?.toLowerCase() === matchedArtist
                        );
                    });

                    // Extract the proper display name
                    if (typeof viewedProfileArtistObj === 'string') {
                        return viewedProfileArtistObj;
                    } else if (viewedProfileArtistObj) {
                        return (viewedProfileArtistObj as any).displayText ||
                               (viewedProfileArtistObj as any).name ||
                               matchedArtist;
                    }

                    return matchedArtist; // Fallback to lowercase version
                });

            if (matchingArtists.length > 0) {
                // Return a random matching artist
                const randomIndex = Math.floor(Math.random() * matchingArtists.length);
                const result: CommonInterestType = {
                    type: 'artist',
                    value: matchingArtists[randomIndex]
                };

                // If we're pre-loading, store the result
                if (isPreLoading) {
                    setPreLoadedCommonInterests((prev: CommonInterestsMap) => ({
                        ...prev,
                        [viewedProfile._id]: result
                    }));
                }

                return result;
            }
        }

        // If no matching artists, check for matching genres
        if (currentUserProfile.favGenres?.length && viewedProfile.favGenres?.length) {
            const currentUserGenres = currentUserProfile.favGenres.map((genre: string) =>
                typeof genre === 'string' ? genre.toLowerCase() : '').filter(Boolean);

            const viewedProfileGenres = viewedProfile.favGenres.map((genre: string) =>
                typeof genre === 'string' ? genre.toLowerCase() : '').filter(Boolean);

            // Find matching genres by comparing lowercase names
            const matchingGenres = currentUserGenres
                .filter((userGenre: string) => viewedProfileGenres.includes(userGenre))
                .map((matchedGenre: string) => {
                    // Find the matched genre with original casing from viewed profile
                    const index = viewedProfileGenres.findIndex(g => g === matchedGenre);
                    return index >= 0 && viewedProfile.favGenres
                        ? viewedProfile.favGenres[index]
                        : matchedGenre;
                });

            if (matchingGenres.length > 0) {
                // Return a random matching genre
                const randomIndex = Math.floor(Math.random() * matchingGenres.length);
                const result: CommonInterestType = {
                    type: 'genre',
                    value: matchingGenres[randomIndex]
                };

                // If we're pre-loading, store the result
                if (isPreLoading) {
                    setPreLoadedCommonInterests((prev: CommonInterestsMap) => ({
                        ...prev,
                        [viewedProfile._id]: result
                    }));
                }

                return result;
            }
        }

        // No common interests found
        return null;
    };

    // Pre-load common interests for first 3 profiles. A profile compared with
    // itself matches every artist, so the owner's preview would show a "Fan of"
    // chip that other people do not see.
    useEffect(() => {
        if (!isOwnProfile && profileIndex < 3 && loggedInUserProfile) {
            findCommonInterest(loggedInUserProfile, profile, true);
        }
    }, [profileIndex, profile, loggedInUserProfile, isOwnProfile]);

    const commonInterest = isOwnProfile
        ? null
        : findCommonInterest(loggedInUserProfile, profile, profileIndex < 3);

    // Fetch similarity config when component mounts
    useEffect(() => {
        const fetchSimilarityConfig = async () => {
            try {
                const config = await getSimilarityConfig();
                if (config && config.NEW_USER_DAYS_THRESHOLD) {
                    setNewUserThreshold(config.NEW_USER_DAYS_THRESHOLD);
                }
            } catch (error) {
                console.error('Error fetching similarity config:', error);
            }
        };

        fetchSimilarityConfig();
    }, []);

    const handleLike = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (!user?._id || !profile.userId) return;
        try {
            if (likeStatus) {
                await unlikeUser(profile.userId);
                await applyLikedUser(queryClient, profile.userId, false);
                // Call onUnlike callback if provided
                onUnlike?.(profile.userId);
                toast.success('User unliked', {
                    position: 'bottom-center'
                });
            } else {
                await likeUser(profile.userId);
                await applyLikedUser(queryClient, profile.userId, true);
                toast.success('User liked', {
                    position: 'bottom-center'
                });
            }
        } catch (error) {
            console.error('Error liking/unliking user:', error);
            toast.error('Failed to like/unlike user. Please try again.');
        }
    };

    const newUserLookupDate = new Date();
    newUserLookupDate.setDate(newUserLookupDate.getDate() - newUserThreshold);
    const isNewUser = profile?.createdAt && (new Date(profile?.createdAt) > newUserLookupDate);

    // Check if user is boosted
    const isBoosted = profile?.isCurrentlyBoosted || false;
    const newUserClass = isNewUser && !isBoosted ? 'ring-4 ring-purple-500 ring-offset-2' : '';
    const boostedUserClass = isBoosted ? 'ring-4 ring-amber-500 ring-offset-2' : '';

    // Function to check if a skill matches with logged-in user's skills
    const isMatchingSkill = (skillToCheck: { _id: string; name?: string; displayName?: string }) => {
        if (!loggedInUserProfile?.skills || loggedInUserProfile.skills.length === 0) return false;

        return loggedInUserProfile.skills.some(
            userSkill => userSkill._id === skillToCheck._id || userSkill.name === skillToCheck.name
        );
    };

    const avatarSection = (
        <div className={`relative flex-shrink-0 ${isGridView ? 'h-10 w-10 md:h-12 md:w-12' : isMobile ? 'h-14 w-14' : 'h-20 w-20'}`}>
            {!hideLikeButton && (
                <div className={'absolute z-[49] top-[-15px] left-[-15px]'}>
                    <LikeButton
                        handleLike={handleLike}
                        likeStatus={likeStatus}
                        size="small"
                    />
                </div>
            )}
            {isNewUser && !isBoosted && (
                <span className={`absolute z-10 flex items-center justify-center ${
                    isGridView
                        ? '!right-[-5px] !top-[-5px] h-6 w-6 p-1.5'
                        : isMobile
                            ? '!right-[-5px] !top-[-5px] h-5 w-5 p-1.5 text-[8px]'
                            : '!right-[-8px] !top-[-8px] h-6 w-6 p-3'
                } bg-purple-500 text-white ${!isMobile ? 'text-[10px]' : 'text-[8px]'} font-semibold rounded-full shadow-md hover:scale-110 transition-transform duration-200`}>
                    New
                </span>
            )}
            {isBoosted && (
                <span className={`absolute z-[49] flex items-center justify-center ${
                    isGridView
                        ? '!right-[-7px] !top-[-7px] h-5 w-5 p-0.5'
                        : isMobile
                            ? '!right-[-5px] !top-[-5px] h-7 w-7 p-1.5 text-[8px]'
                            : '!right-[-8px] !top-[-8px] h-8 w-8 p-1.5'
                } bg-amber-500 text-white ${!isMobile ? 'text-[10px]' : 'text-[8px]'} font-semibold rounded-full shadow-md hover:scale-110 transition-transform duration-200`}>
                    <Zap size={isMobile ? 14 : 18} />
                </span>
            )}
            <AvatarImage
                src={profile.profilePhoto}
                alt={profile.displayName || profile.username || 'Profile photo'}
                wrapperClass={`${boostedUserClass} ${newUserClass} !rounded-full overflow-hidden`}
            />
        </div>
    );

    // Replace the display name logic with our utility function
    const displayName = getUserDisplayName({
        displayName: profile.displayName,
        firstName: profile.firstName,
        lastName: profile.lastName,
        username: profile.username
    });

    const profileInfoSection = isGridView ? (
    // Grid view - specific layout with controlled line heights
        <div className="flex flex-col justify-start ml-2 min-w-0">
            {/* Line 1: Name */}
            <div className="font-sans text-eerieblack text-sm font-medium truncate max-w-full">
                {displayName}
            </div>

            {/* Line 2-3: Headline (up to 2 lines) */}
            <div className="font-sans font-bold text-xs text-indigo-500 line-clamp-2 h-[32px] overflow-hidden">
                {profile.headline || ''}
            </div>

            {/* Line 4: Location (only if present, no extra space if missing) */}
            {profile?.location?.displayText ? (
                <button
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (profile?.location?.displayText) {
                            navigate(`/search?query=${profile.location.displayText}`);
                        }
                    }}
                    className="font-sans font-bold text-xs text-muted text-eerieblack truncate max-w-full block h-[16px] text-left hover:text-indigo-500 transition-colors"
                >
                    {profile.location.displayText}
                </button>
            ) : null}

            {/* Common interest button for grid view - now below location */}
            {commonInterest && (
                <div className="mt-0.5 max-w-full overflow-hidden">
                    <CommonInterestButton
                        commonInterest={commonInterest}
                        isGridView={true}
                        isMobile={isMobile}
                    />
                </div>
            )}
        </div>
    ) : (
    // List view layout - responsive for mobile but unaltered for desktop
        <div className="flex flex-col justify-center ml-3 min-w-0">
            <div className="flex items-center min-w-0">
                <div className={isMobile ? 'font-sans text-eerieblack text-base font-medium truncate' : 'font-sans text-eerieblack text-base font-medium min-w-0 break-words'}>
                    {displayName}
                </div>
            </div>

            <div className={isMobile ? 'font-sans font-bold text-base text-indigo-500 break-words line-clamp-2' : 'font-sans font-bold text-md text-indigo-500 break-words'}>
                {profile.headline || ''}
            </div>

            {profile?.location?.displayText && (
                <button
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (profile?.location?.displayText) {
                            navigate(`/search?query=${profile.location.displayText}`);
                        }
                    }}
                    className={isMobile ? 'font-sans font-bold text-[13px] text-muted text-eerieblack truncate text-left hover:text-indigo-500 transition-colors' : 'font-sans font-bold text-sm text-muted text-eerieblack block max-w-full break-words text-left hover:text-indigo-500 transition-colors'}
                >
                    {profile?.location?.displayText || ''}
                </button>
            )}

            {/* Common interest button for list view - now below location */}
            {commonInterest && (
                <div className="mt-1">
                    <CommonInterestButton
                        commonInterest={commonInterest}
                        isGridView={false}
                        isMobile={isMobile}
                    />
                </div>
            )}
        </div>
    );

    const formatSkillName = (name: string | undefined) => {
        if (!name) return ''; // Return an empty string if name is undefined
        return name
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const hasMoreSkills = profile.skills && visibleSkillCount !== null && profile.skills.length > visibleSkillCount;
    const skillsCount = profile.skills?.length || 0;
    const skillsToShow = visibleSkillCount !== null ? visibleSkillCount : undefined;

    const skillsSection = (
        <div
            ref={skillsContainerRef}
            className={`relative w-full max-w-full min-w-0 ${isGridView ? 'min-h-[48px] max-h-[48px]' : ''} ${isGridView ? 'group' : ''}`}
        >
            {/* Always show "+" indicator if there are more skills than we can display */}
            {isGridView && hasMoreSkills && (
                <div className="absolute right-0 top-0 bg-gray-200 text-gray-700 rounded-full h-6 w-6 flex items-center justify-center text-xs font-medium z-10 group-hover:opacity-0 transition-opacity duration-200 border border-gray-300">
          +{skillsCount - (skillsToShow || 0)}
                </div>
            )}

            <div className={`flex w-full max-w-full min-w-0 flex-wrap gap-1 ${isGridView && hasMoreSkills ? 'max-h-[48px] group-hover:max-h-[120px] overflow-hidden group-hover:overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent pr-1 transition-all duration-200' : ''}`}>
                {profile.skills
                    ?.slice() // Create a copy to avoid mutating the original array
                    .sort((a, b) => {
                        const aMatches = isMatchingSkill(a);
                        const bMatches = isMatchingSkill(b);
                        // Sort matching skills first
                        if (aMatches && !bMatches) return -1;
                        if (!aMatches && bMatches) return 1;
                        return 0;
                    })
                // Show all skills, scrolling will handle overflow
                    .map((skill) => {
                        const label = formatSkillName(skill.displayName || skill.name);
                        return (
                            <button
                                type="button"
                                key={skill._id}
                                className={`${profileSkillChipClassName(!isOwnProfile && isMatchingSkill(skill))} ${isGridView ? '!px-2 !py-0.5 !text-xs' : '!px-2.5 !py-1 !text-xs'}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    navigate(skillChipHref(skill.displayName || skill.name, !!user));
                                }}
                            >
                                {label}
                            </button>
                        );
                    })}
            </div>
        </div>
    );

    // Get description from either field
    const description = profile.description || profile.bio;

    // Show description only in list view with responsive size for mobile
    const descriptionSection = !isGridView && description ? (
        <div className={isMobile ? 'font-sans text-base line-clamp-2 text-eerieblack mr-8' : 'font-sans text-s mt-2 text-eerieblack line-clamp-4 mr-8'}>
            {description}
        </div>
    ) : null;

    const handleHighlightClick = (highlight: MiniJWCarouselSlideProps) => {
        if (onHighlightClick) {
            const modalNavigableHighlights = getModalNavigableHighlights(
                displayHighlights as HighlightType[]
            );
            const modalIndex = getModalCurrentIndex(
                modalNavigableHighlights,
                highlight as HighlightType
            );
            onHighlightClick(
                highlight as HighlightType,
                modalIndex,
                profile._id,
                modalNavigableHighlights
            );
        }
    };

    const resolveJamBoardPinColor = (
        colorKeyOrHex: string | null | undefined,
        pinId: string
    ): string => {
        const JAMBOARD_PIN_COLOR_HEX: Record<string, string> = {
            red: '#dc2626',
            orange: '#ea580c',
            yellow: '#ca8a04',
            lime: '#65a30d',
            green: '#16a34a',
            teal: '#0d9488',
            cyan: '#0891b2',
            blue: '#2563eb',
            indigo: '#4f46e5',
            violet: '#7c3aed',
            magenta: '#db2777',
            black: '#111827',
        };
        const keys = Object.keys(JAMBOARD_PIN_COLOR_HEX);
        let hash = 0;
        for (let i = 0; i < pinId.length; i += 1) {
            hash = (Math.imul(31, hash) + pinId.charCodeAt(i)) >>> 0;
        }
        const fallback = JAMBOARD_PIN_COLOR_HEX[keys[hash % keys.length]] || '#dc2626';
        if (!colorKeyOrHex) return fallback;
        const normalized = colorKeyOrHex.trim().toLowerCase();
        if (normalized.startsWith('#')) return normalized;
        return JAMBOARD_PIN_COLOR_HEX[normalized] || fallback;
    };

    const formatJamBoardRateLabel = (
        paymentText: string | null | undefined,
        paymentCurrency: string | null | undefined,
        paymentCadence?: string | null
    ): string | undefined => {
        const rawText = paymentText?.trim();
        if (!rawText) return 'FREE';
        const numericMatch = rawText.match(/\d+(?:[.,]\d+)?/);
        if (!numericMatch) return 'FREE';
        const numericValue = Number(numericMatch[0].replace(/,/g, ''));
        if (!Number.isFinite(numericValue) || numericValue <= 0) return 'FREE';
        const formattedNumber = Number.isInteger(numericValue)
            ? numericValue.toLocaleString('en-US')
            : numericValue.toLocaleString('en-US', { maximumFractionDigits: 2 });
        const currencySymbolForCode = (code: string): string | null => {
            const normalized = code.toUpperCase();
            if (normalized === 'USD') return '$';
            if (normalized === 'EUR') return '€';
            if (normalized === 'GBP') return '£';
            if (normalized === 'CAD') return 'C$';
            if (normalized === 'AUD') return 'A$';
            if (normalized === 'NZD') return 'NZ$';
            if (normalized === 'JPY') return '¥';
            return null;
        };
        const inlineCurrencyMatch = rawText.match(/\b(USD|EUR|GBP|CAD|AUD|NZD|JPY)\b/i);
        const resolvedCurrency = paymentCurrency || inlineCurrencyMatch?.[1] || 'USD';
        const symbol = currencySymbolForCode(resolvedCurrency) || '$';
        const amount = `${symbol}${formattedNumber}`;
        return paymentCadence === 'hourly' ? `${amount}/hr` : amount;
    };

    const displayHighlights = React.useMemo(() => {
        const realHighlights = profile.highlights || [];
        const sortedActiveJamBoardPins = allJamBoardPins
            .filter((pin: JamBoardPin) => String(pin.author.userId) === String(profile.userId))
            .sort(
                (a: JamBoardPin, b: JamBoardPin) =>
                    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );
        const jamBoardPinsDisplayMode = profile.jamBoardPinsDisplayMode || 'most_recent';
        if (jamBoardPinsDisplayMode === 'none' || sortedActiveJamBoardPins.length === 0) {
            return realHighlights;
        }
        const hiddenJamBoardPinIds = new Set(
            (profile.hiddenJamBoardPinIds || []).map((id) => String(id))
        );
        const jamBoardPinsToDisplay =
            jamBoardPinsDisplayMode === 'all'
                ? sortedActiveJamBoardPins.filter(
                    (pin: JamBoardPin) => !hiddenJamBoardPinIds.has(String(pin._id))
                )
                : [sortedActiveJamBoardPins[0]];
        if (!jamBoardPinsToDisplay.length) {
            return realHighlights;
        }
        const jamBoardVirtualHighlights = jamBoardPinsToDisplay.map((pin: JamBoardPin) => ({
            url: '',
            image: undefined,
            imagePath: '',
            description: pin.title,
            mediaUrl: '',
            coverArtUrl: pin.projectImage || '',
            typeCd: 'JB',
            canEdit: false,
            isVirtualJamBoardHighlight: true,
            jamBoardLocationText:
                pin.locationMode === 'remote'
                    ? 'Remote'
                    : pin.location?.displayText || 'Location on profile',
            jamBoardPinColor: resolveJamBoardPinColor(pin.pinColor, pin._id),
            jamBoardRateLabel: formatJamBoardRateLabel(
                pin.paymentText,
                pin.paymentCurrency,
                pin.paymentCadence
            ),
        }));
        return [...jamBoardVirtualHighlights, ...realHighlights];
    }, [allJamBoardPins, profile.highlights, profile.hiddenJamBoardPinIds, profile.jamBoardPinsDisplayMode, profile.userId]);

    // For grid view, use a fixed height card with consistent layout
    if (isGridView) {
        return (
            <Link
                className="flex flex-col bg-white rounded-lg p-2 h-full w-full overflow-visible shadow-sm hover:shadow-md transition-shadow duration-200 group"
                to={`/${profile?.username}`}
                onClick={blockNestedControls}
            >
                <div className="flex items-start space-x-2 overflow-visible">
                    {avatarSection}
                    <div className="flex-1 min-w-0">
                        {profileInfoSection}
                    </div>
                </div>

                {/* Visual separator between profile info and skills */}
                <div className="mt-1 w-full h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />

                {/* Lines 5-7: Skills section with adequate spacing */}
                <div className="mt-1.5 flex-grow">
                    {skillsSection}
                </div>
            </Link>
        );
    } else if (isMobile) {
    // Mobile-specific list view with optimized layout
        return (
            <Link
                to={`/${profile?.username}`}
                className={`flex flex-col rounded-lg pt-4 pr-3 pb-3 pl-4 hover:bg-gray-50 transition-colors duration-200 relative overflow-hidden no-underline text-inherit ${isMobile ? 'profile-preview-mobile' : ''}`}
                onClick={blockNestedControls}
            >
                <div className={`flex items-start overflow-visible ${isMobile ? 'profile-header-mobile' : ''}`}>
                    {avatarSection}
                    <div className={`flex-1 min-w-0 ${isMobile ? 'profile-content-mobile' : ''}`}>
                        {profileInfoSection}
                    </div>
                </div>

                {/* Skills section with mobile optimization */}
                <div className={`mt-2 ${isMobile ? 'skills-section-mobile' : ''}`}>
                    {skillsSection}
                </div>

                {/* Description with mobile optimization */}
                {description && (
                    <div className={isMobile ? 'description-mobile' : 'mt-1'}>
                        {descriptionSection}
                    </div>
                )}

                {/* Highlights carousel if available */}
                {displayHighlights && displayHighlights.length > 0 ? (
                    <div className={isMobile ? 'carousel-container-mobile' : 'mt-3'}>
                        <div className={`w-full aspect-[4/3] relative overflow-visible rounded-lg ${isMobile ? 'carousel-touch-pan-y' : ''}`}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                        >
                            <div className="absolute inset-0 w-full h-full flex items-center justify-center">
                                <MiniJWCarousel
                                    id={`profile-${profile._id}-highlights`}
                                    slides={displayHighlights as MiniJWCarouselSlideProps[]}
                                    onSlideClick={handleHighlightClick}
                                    slidesPerView={1}
                                    slidesPerGroup={1}
                                    isLoading={false}
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className={isMobile ? 'carousel-container-mobile' : 'mt-3'}>
                        {isMobile ? (
                            <div className="w-full h-[5px]" /> // Invisible spacer for mobile
                        ) : (
                            <div className="w-full aspect-[4/3] relative overflow-hidden rounded-lg" >
                                <div className="absolute inset-0 w-full h-full flex items-center justify-center">
                                    <div className="mini-carousel-container w-full h-full">
                                        <div className="mini-highlight-card w-full h-full">
                                            <div className="absolute inset-0 w-full h-full flex items-center justify-center rounded-lg bg-gray-100">
                                                <span className="text-gray-400 text-sm">No highlights yet</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Link>
        );
    } else {
    // Desktop list view. One skills block and one description; profilePreviewList.css places them.
        return (
            <Link
                className='profile-list-card bg-white hover:bg-gray-50 transition-colors duration-200'
                to={`/${profile?.username}`}
                onClick={blockNestedControls}
            >
                <div className='profile-list-card__layout'>
                    <div className='profile-list-card__identity'>
                        {avatarSection}
                        {profileInfoSection}
                    </div>

                    <div className='profile-list-card__highlight'>
                        <div className="absolute inset-0 w-full h-full">
                            {displayHighlights && displayHighlights.length > 0 ? (
                                <MiniJWCarousel
                                    id={`profile-${profile._id}-highlights`}
                                    slides={displayHighlights as MiniJWCarouselSlideProps[]}
                                    onSlideClick={handleHighlightClick}
                                    isLoading={false}
                                    slidesPerView={1}
                                    slidesPerGroup={1}
                                />
                            ) : (
                                <div className="absolute inset-0 w-full h-full flex items-center justify-center">
                                    <div className="mini-carousel-container w-full h-full">
                                        <div className="mini-highlight-card w-full h-full">
                                            <div className="absolute inset-0 w-full h-full flex items-center justify-center rounded-lg bg-gray-100">
                                                <span className="text-gray-400 text-sm">No highlights yet</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className='profile-list-card__skills'>
                        {skillsSection}
                    </div>
                    <div className={`profile-list-card__description font-sans text-s ${description ? 'text-eerieblack' : 'text-gray-300 italic'}`}>
                        {description || 'No description provided'}
                    </div>
                </div>
            </Link>
        );
    }
};

export default memo(ProfilePreview, areEqual);
